import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";

export type ProfileRole = "sales" | "sales_manager" | "admin";

export interface CurrentProfile {
  id: string;
  email: string;
  full_name: string | null;
  role: ProfileRole | string;
}

interface CurrentProfileContextType {
  profile: CurrentProfile | null;
  /** Rôle `sales_manager` (Jérémie). Les fonctions manager sont réservées à ce rôle. */
  isManager: boolean;
  loading: boolean;
}

const CurrentProfileContext = createContext<CurrentProfileContextType | undefined>(undefined);

export function CurrentProfileProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const [profile, setProfile] = useState<CurrentProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void supabase
      .from("profiles")
      .select("id,email,full_name,role")
      .eq("id", userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error("[CurrentProfile] Chargement du profil impossible :", error.message);
        setProfile((data as CurrentProfile | null) ?? null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, userId]);

  const value = useMemo<CurrentProfileContextType>(
    () => ({ profile, isManager: profile?.role === "sales_manager", loading: authLoading || loading }),
    [profile, authLoading, loading],
  );

  return <CurrentProfileContext.Provider value={value}>{children}</CurrentProfileContext.Provider>;
}

export function useCurrentProfile(): CurrentProfileContextType {
  const context = useContext(CurrentProfileContext);
  if (!context) throw new Error("useCurrentProfile doit être utilisé dans un CurrentProfileProvider");
  return context;
}
